import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HeaderNav } from "../components/HeaderNav";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { RESUME_URL } from "../lib/data";
import { ArrowUpRight, Download, Home, Shield, TrendingUp, Users, Megaphone, BarChart3 } from "lucide-react";
import { ZoomImage } from "../components/ZoomImage";

const NAVY = "#1B466F";
const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};
const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}>{children}</motion.span>
  </span>
);
const Chapter = ({ n, label, title, children }) => (
  <section className="max-w-5xl mx-auto px-6 py-14 border-t border-black/5">
    <motion.div {...reveal} className="grid md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-5xl text-primary/20 leading-none">{n}</span>
          <span className="overline text-primary">{label}</span>
        </div>
        {title && <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-5 leading-tight">{title}</h2>}
      </div>
      <div className="md:col-span-7 md:col-start-6 text-lg text-foreground/70 leading-relaxed space-y-4">{children}</div>
    </motion.div>
  </section>
);
const Bullets = ({ items }) => (
  <ul className="space-y-2.5">{items.map((p) => (
    <li key={p} className="flex gap-3 text-base"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" /><span>{p}</span></li>
  ))}</ul>
);

/* Native dashboard frame in the product's enterprise style */
const Dash = ({ title, children, caption }) => (
  <div>
    <div className="rounded-2xl overflow-hidden border border-black/10 card-shadow bg-[#F4F6F8]">
      <div className="px-5 py-3 text-white flex items-center justify-between" style={{ background: NAVY }}>
        <span className="text-[11px] font-mono uppercase tracking-widest opacity-70">Creator Analytics</span>
        <span className="font-medium text-sm">{title}</span>
        <span className="text-[10px] opacity-60">Data Source: Social Analytics</span>
      </div>
      <div className="p-5">{children}</div>
    </div>
    {caption && <div className="mt-3 text-center text-[11px] font-mono uppercase tracking-widest text-foreground/40">{caption}</div>}
  </div>
);
const KPI = ({ v, l }) => (
  <div className="rounded-lg bg-white border border-black/5 p-3">
    <div className="font-serif text-2xl text-foreground leading-none">{v}</div>
    <div className="text-[10px] text-foreground/50 mt-1.5 uppercase tracking-wide font-mono">{l}</div>
  </div>
);
const Funnel = ({ steps }) => (
  <div className="flex items-end gap-1.5 h-24">
    {steps.map((s, i) => (
      <div key={s.l} className="flex-1 flex flex-col items-center justify-end">
        <div className="w-full rounded-t" style={{ height: `${100 - i * 22}%`, background: `rgba(27,70,111,${1 - i * 0.18})` }} />
        <div className="text-[9px] text-foreground/50 mt-1 font-mono uppercase">{s.l}</div>
      </div>
    ))}
  </div>
);
const ScoreBar = ({ l, v }) => (
  <div>
    <div className="flex justify-between text-[11px] mb-1"><span className="text-foreground/70">{l}</span><span className="text-foreground/50 font-mono">{v}/100</span></div>
    <div className="h-2 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: NAVY }} /></div>
  </div>
);

const ScreenExecutive = () => (
  <Dash title="Executive Summary" caption="Executive Summary · programme performance">
    <div className="grid grid-cols-4 gap-2.5">
      <KPI v="$1.2M" l="Budget spent" /><KPI v="49M" l="Reach" /><KPI v="405K" l="Engagement" /><KPI v="20K" l="Clicks" />
      <KPI v="5.0K" l="Conversions" /><KPI v="$240" l="Cost / conv." /><KPI v="3.4x" l="ROI" /><KPI v="128" l="Creators" />
    </div>
    <div className="mt-4 grid grid-cols-3 gap-3">
      <div className="col-span-2 rounded-lg bg-white border border-black/5 p-3">
        <div className="text-[10px] text-foreground/50 uppercase font-mono mb-2">Performance funnel</div>
        <Funnel steps={[{ l: "Reach" }, { l: "Engage" }, { l: "Clicks" }, { l: "Conv." }]} />
      </div>
      <div className="rounded-lg text-white p-3" style={{ background: NAVY }}>
        <div className="text-[10px] uppercase font-mono opacity-70 mb-1.5">Top insight</div>
        <p className="text-[11px] leading-relaxed opacity-90">Two platforms and four creators drove most conversions this period.</p>
      </div>
    </div>
  </Dash>
);
const ScreenCreator = () => (
  <Dash title="Creator Scorecard" caption="Creator Performance · multidimensional quality">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-12 w-12 rounded-full" style={{ background: NAVY }} />
      <div><div className="font-medium text-foreground text-sm">Creator A</div><div className="text-[10px] text-foreground/50 font-mono">Tech · Tier 2 · 412K followers</div></div>
      <div className="ml-auto text-right"><div className="font-serif text-2xl" style={{ color: NAVY }}>84</div><div className="text-[9px] text-foreground/50 uppercase font-mono">Overall</div></div>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
      <ScoreBar l="Reliability" v={88} /><ScoreBar l="Performance consistency" v={79} />
      <ScoreBar l="Audience authenticity" v={91} /><ScoreBar l="Category fit" v={82} />
      <ScoreBar l="Price efficiency" v={74} /><ScoreBar l="Audience relevance" v={86} />
    </div>
  </Dash>
);
const ScreenCampaign = () => (
  <Dash title="Campaign Performance" caption="Campaign Performance · investment vs. outcome">
    <div className="grid grid-cols-4 gap-2.5">
      <KPI v="$320K" l="Budget" /><KPI v="14M" l="Reach" /><KPI v="1.4K" l="Conversions" /><KPI v="22" l="Creators" />
    </div>
    <div className="mt-4 grid grid-cols-3 gap-3">
      <div className="rounded-lg bg-white border border-black/5 p-3">
        <div className="text-[10px] text-foreground/50 uppercase font-mono mb-2">Sentiment</div>
        <div className="flex h-3 rounded-full overflow-hidden"><div className="w-[64%]" style={{ background: "#2E7D57" }} /><div className="w-[22%] bg-black/15" /><div className="w-[14%]" style={{ background: "#C0492F" }} /></div>
        <div className="flex justify-between text-[9px] text-foreground/50 font-mono mt-1"><span>64% pos</span><span>14% neg</span></div>
      </div>
      <div className="rounded-lg bg-white border border-black/5 p-3">
        <div className="text-[10px] text-foreground/50 uppercase font-mono mb-2">Funnel</div>
        <Funnel steps={[{ l: "Reach" }, { l: "Eng." }, { l: "Conv." }]} />
      </div>
      <div className="rounded-lg bg-white border border-black/5 p-3">
        <div className="text-[10px] text-foreground/50 uppercase font-mono mb-2">Top creators</div>
        {["Creator A", "Creator D", "Creator K"].map((c, i) => (
          <div key={c} className="flex items-center justify-between text-[11px] py-0.5"><span className="text-foreground/70">{c}</span><span className="font-mono text-foreground/40">{38 - i * 9}%</span></div>
        ))}
      </div>
    </div>
  </Dash>
);

const CHALLENGES = [
  "Understanding whether the overall creator programme was delivering value",
  "Identifying the best-performing campaigns, creators and posts",
  "Comparing metrics against previous periods and benchmarks",
  "Evaluating creators beyond follower count and engagement",
  "Presenting dense analytical information without overwhelming users",
];
const QUESTIONS = [
  [TrendingUp, "Is the creator programme delivering value?"],
  [BarChart3, "Which posts and content formats perform best?"],
  [Users, "Which creators should the company reinvest in?"],
  [Megaphone, "Which campaigns are driving meaningful results?"],
];

export default function CreatorPerformanceCaseStudy() {
  useEffect(() => {
    document.title = "Creator Performance Analytics — Case Study · Vatushi Moondra";
    return () => { document.title = "Vatushi Moondra — Senior UX Designer"; };
  }, []);
  return (
    <div className="relative bg-background">
      <HeaderNav overDark />
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden bg-[#0A0A0C] pt-28 pb-16">
        <div className="absolute inset-0" style={{ background: "radial-gradient(55% 60% at 25% 30%, rgba(99,102,241,0.26), transparent 70%)" }} />
        <svg className="absolute right-6 top-1/3 opacity-40 hidden md:block" width="420" height="220" viewBox="0 0 420 220">
          {[60, 110, 90, 150, 130, 190, 170].map((h, i) => <rect key={i} x={i * 58 + 10} y={220 - h} width="34" height={h} rx="4" fill="#6366F1" fillOpacity={0.25 + i * 0.08} />)}
        </svg>
        <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A5B4FC]">Enterprise Analytics · Anonymized case study</motion.span>
          <h1 className="mt-6 font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight">
            <MaskLine delay={0.15}>From scattered metrics</MaskLine>
            <MaskLine delay={0.28}>to <span className="text-[#A5B4FC] italic">clear investment</span></MaskLine>
            <MaskLine delay={0.41}>decisions.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
            An enterprise analytics platform for a global technology company — measuring creator,
            campaign and content performance across one connected dashboard experience.
          </motion.p>
        </div>
      </section>

      <div className="border-y border-black/5 bg-secondary/40 py-5 font-mono text-sm uppercase tracking-[0.18em] text-foreground/70">
        <Marquee items={["Executive Summary", "Post Performance", "Creator Performance", "Campaign Performance", "Design System"]} />
      </div>

      {/* Info */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[["Client", "Global technology company"], ["Role", "Product / UX Designer"], ["Platform", "Enterprise web app"], ["Timeline", "1 week"]].map(([k, v], i) => (
            <motion.div key={k} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}>
              <div className="overline text-primary/70">{k}</div><div className="text-foreground mt-1.5 leading-snug">{v}</div>
            </motion.div>
          ))}
        </div>
        <div className="mt-5 text-sm text-foreground/50"><span className="font-medium text-foreground/70">Users:</span> marketing leaders, campaign managers and creator-partnership teams. <span className="font-medium text-foreground/70">Four views:</span> Executive Summary · Post Performance · Creator Performance · Campaign Performance.</div>
      </section>

      <Chapter n="01" label="Overview" title="One connected dashboard experience.">
        <p>Creator marketing data was spread across campaigns, social platforms, creators and individual posts. The project brought this information into a single, connected dashboard experience with four views — Executive Summary, Post Performance, Creator Performance and Campaign Performance.</p>
      </Chapter>

      <Chapter n="02" label="Design Problem" title="Plenty of data — no simple way to interpret it.">
        <p>Stakeholders had extensive performance data but lacked a simple way to interpret it. The key challenges:</p>
        <Bullets items={CHALLENGES} />
        <div className="rounded-2xl bg-[#0A0A0C] text-white p-6 mt-3">
          <div className="overline text-[#A5B4FC] mb-2">How might we</div>
          <p className="font-serif text-xl md:text-2xl leading-snug">…help marketing teams move from scattered metrics to clear investment decisions?</p>
        </div>
      </Chapter>

      <Chapter n="03" label="Process" title="Start with decisions, not data points.">
        <p>I organised stakeholder requirements around four business questions:</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {QUESTIONS.map(([Ic, q]) => (
            <div key={q} className="rounded-2xl border border-black/5 bg-white p-4 card-shadow flex gap-3 items-start">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Ic size={16} /></span>
              <span className="text-base text-foreground/75">{q}</span>
            </div>
          ))}
        </div>
        <p>Metrics were grouped into awareness, engagement, conversion, cost efficiency, creator quality and audience relevance — then structured into a progressive hierarchy:</p>
        <div className="flex flex-wrap items-center gap-2 font-mono text-sm">
          {["Programme", "Campaign", "Creator", "Post"].map((s, i) => (
            <React.Fragment key={s}>
              <span className="rounded-full bg-secondary text-primary px-3 py-1">{s}</span>
              {i < 3 && <span className="text-primary/40">→</span>}
            </React.Fragment>
          ))}
        </div>
        <p>This let users start with an overall summary and gradually investigate the source of a performance result.</p>
      </Chapter>

      <Chapter n="04" label="Wireframes" title="A consistent layout across all four dashboards.">
        <p>Structural wireframes established the same hierarchy on every screen:</p>
        <Bullets items={["Global filters", "Selected campaign or creator context", "Primary KPIs", "Key performance insight", "Charts and visual analysis", "Detailed data tables"]} />
        <p>They also explored comparison states — previous period, previous year, campaign target, creator historical average, similar creator tier and category benchmark — carried into the final UI.</p>
      </Chapter>

      {/* UI Design */}
      <section className="border-t border-black/5 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <div className="flex items-baseline gap-4"><span className="font-serif text-5xl text-primary/20 leading-none">05</span><span className="overline text-primary">UI Design</span></div>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">Four views, one system.</h2>
            <p className="mt-4 text-lg text-foreground/70 leading-relaxed">The four delivered dashboards — Executive Summary, Post Performance, Creator Performance and Campaign Performance — shown as designed and anonymized.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="creatorperf-gallery">
            {[
              ["exec.jpg", "Executive Summary · programme-level performance"],
              ["post.jpg", "Sponsored Post Performance · content analysis"],
              ["creator.jpg", "Creator Performance · multidimensional scorecard"],
              ["campaign.jpg", "Campaign Performance · investment vs. outcome"],
            ].map(([src, cap], i) => (
              <motion.figure key={src} data-testid="creatorperf-screen"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}>
                <div className="rounded-2xl overflow-hidden border border-black/10 card-shadow bg-white">
                  <ZoomImage src={`/projects/creatorperf/${src}`} alt={cap} caption={cap} className="w-full" />
                </div>
                <figcaption className="mt-3 text-center text-[11px] font-mono uppercase tracking-widest text-foreground/40">{cap}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Design system */}
      <section className="max-w-6xl mx-auto px-6 py-14 border-t border-black/5">
        <motion.div {...reveal} className="max-w-3xl mb-8">
          <span className="overline text-primary">Design system</span>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-3 leading-tight">Reusable components across every dashboard.</h2>
        </motion.div>
        <div className="flex flex-wrap gap-2">
          {["KPI cards", "Filters & selectors", "Insight banners", "Score cards", "Funnels & charts", "Status indicators", "Data tables", "Source & refresh labels"].map((c) => (
            <span key={c} className="rounded-full bg-secondary text-secondary-foreground font-mono text-[11px] px-3 py-1.5 border border-black/5">{c}</span>
          ))}
        </div>
      </section>

      {/* Impact */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="rounded-3xl bg-[#0A0A0C] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 60% at 80% 20%, rgba(99,102,241,0.22), transparent 70%)" }} />
            <div className="relative z-10">
              <span className="overline text-[#A5B4FC]">06 · Design Impact</span>
              <h2 className="font-serif text-3xl md:text-5xl mt-4 max-w-3xl leading-tight">A unified framework — from programme level to individual posts.</h2>
              <div className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-white/70">
                {["Reduced dependence on disconnected reports", "Made business performance easier to understand", "Improved comparison between campaigns and creators", "A more complete way to evaluate creator quality", "Connected summary insights with detailed supporting data", "Supports future dashboards through reusable components"].map((p) => (
                  <div key={p} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#A5B4FC]" /><span className="text-sm leading-relaxed">{p}</span></div>
                ))}
              </div>
              <p className="mt-6 text-white/40 text-xs">Described as design outcomes — no quantified impact is claimed without verified project metrics.</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Chapter n="07" label="Learnings">
        <Bullets items={[
          "Begin with decisions, not metrics — structuring around business questions beat showing every data point.",
          "Metrics need context — comparisons against periods, benchmarks and similar creators aid interpretation.",
          "Creator quality is multidimensional — reliability, authenticity, relevance and cost efficiency matter beyond reach.",
          "Executive and operational users need different depths — summary cards vs. charts and tables.",
          "Charts and tables work best together — trends and outliers, verified against underlying data.",
        ]} />
      </Chapter>

      <div className="max-w-5xl mx-auto px-6 pb-4">
        <div className="rounded-2xl bg-secondary px-5 py-4 text-sm text-foreground/70 flex items-start gap-3">
          <Shield size={18} className="text-primary shrink-0 mt-0.5" />
          <span>Anonymized case study. The client is referred to only as a global technology company; brand marks, data-source and creator names have been removed or replaced with generic placeholders.</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-10 mb-6 flex flex-wrap items-center justify-between gap-6 border-t border-black/5 pt-10">
        <Link to="/" data-testid="creatorperf-home-cta" className="inline-flex items-center gap-2 text-primary hover:underline font-medium"><Home size={16} /> Back to home</Link>
        <div className="flex items-center gap-3">
          <Link to="/#projects" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors">More projects <ArrowUpRight size={15} /></Link>
          <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-[hsl(242,47%,28%)] transition-colors"><Download size={15} /> Download Resume</a>
        </div>
      </div>
      <Footer />
    </div>
  );
}
